import { CAM_SIZE, CamLayout, formatTime } from './types'
import { iconSvg } from './icons'

/**
 * The floating recorder bar.
 *
 * Uses Chrome's Document Picture-in-Picture API: a small always-on-top OS window
 * that survives you switching to any other app. Inside it we put the timer,
 * pause/stop, a mic toggle, and — importantly — a mini-map of your screen you
 * can drag to reposition the face bubble without going back to the Bloom tab.
 *
 * Built with plain DOM rather than React: a PiP window is a *separate document*,
 * and hand-rolling ~80 lines of DOM is far less fragile than portalling a React
 * tree across documents and re-injecting the stylesheet.
 */

export interface FloatBarHandlers {
  onPause(): void
  onResume(): void
  onStop(): void
  onToggleMic(): void
  onToggleCam(): void
  onMove(x: number, y: number): void
  onClosed(): void
}

export interface FloatBarState {
  recording: boolean
  paused: boolean
  elapsed: number
  micMuted: boolean
  micLevel: number
  layout: CamLayout
  aspect: number
}

export const floatBarSupported = () => 'documentPictureInPicture' in window

export class FloatBar {
  private win: Window | null = null
  private els: Record<string, HTMLElement> = {}
  private handlers: FloatBarHandlers

  constructor(handlers: FloatBarHandlers) {
    this.handlers = handlers
  }

  get isOpen() { return !!this.win && !this.win.closed }

  async open(cameraStream: MediaStream | null, initial: FloatBarState) {
    if (this.isOpen) return
    const api = (window as unknown as {
      documentPictureInPicture?: { requestWindow(o: object): Promise<Window> }
    }).documentPictureInPicture
    if (!api) throw new Error('unsupported')

    const win = await api.requestWindow({ width: 260, height: 300, disallowReturnToOpener: true })
    this.win = win
    win.addEventListener('pagehide', () => {
      this.win = null
      this.handlers.onClosed()
    })

    const style = win.document.createElement('style')
    style.textContent = CSS
    win.document.head.appendChild(style)
    win.document.body.innerHTML = HTML
    const $ = (id: string) => win.document.getElementById(id) as HTMLElement
    this.els = {
      cam: $('fb-cam'), dot: $('fb-dot'), time: $('fb-time'), pause: $('fb-pause'),
      stop: $('fb-stop'), mic: $('fb-mic'), camBtn: $('fb-camtoggle'),
      map: $('fb-map'), pip: $('fb-pip'), level: $('fb-level'),
    }

    const camEl = this.els.cam as HTMLVideoElement
    camEl.muted = true
    camEl.autoplay = true
    ;(camEl as HTMLVideoElement).playsInline = true
    if (cameraStream) {
      camEl.srcObject = cameraStream
      void camEl.play().catch(() => {})
    }

    this.els.pause.addEventListener('click', () => {
      if (this.els.pause.dataset.paused === 'true') this.handlers.onResume()
      else this.handlers.onPause()
    })
    this.els.stop.addEventListener('click', () => this.handlers.onStop())
    this.els.mic.addEventListener('click', () => this.handlers.onToggleMic())
    this.els.camBtn.addEventListener('click', () => this.handlers.onToggleCam())

    this.wireMapDrag()
    this.update(initial)
  }

  /** Dragging inside the mini-map moves the bubble in the real recording. */
  private wireMapDrag() {
    const map = this.els.map
    const move = (e: PointerEvent) => {
      const r = map.getBoundingClientRect()
      const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width))
      const y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))
      this.handlers.onMove(x, y)
    }
    map.addEventListener('pointerdown', (e) => {
      map.setPointerCapture((e as PointerEvent).pointerId)
      move(e as PointerEvent)
    })
    map.addEventListener('pointermove', (e) => {
      if ((e as PointerEvent).pressure > 0 || (e as PointerEvent).buttons) move(e as PointerEvent)
    })
  }

  update(s: FloatBarState) {
    if (!this.isOpen) return
    const { els } = this
    els.time.textContent = formatTime(s.elapsed)
    els.dot.className = s.paused ? 'dot paused' : 'dot rec'
    els.pause.dataset.paused = String(s.paused)
    els.pause.innerHTML = iconSvg(s.paused ? 'play' : 'pause', 15)
    els.pause.setAttribute('aria-label', s.paused ? 'Resume recording' : 'Pause recording')
    els.mic.classList.toggle('off', s.micMuted)
    els.mic.innerHTML = iconSvg(s.micMuted ? 'micOff' : 'mic', 15)
    els.camBtn.classList.toggle('off', s.layout.hidden)
    els.camBtn.innerHTML = iconSvg(s.layout.hidden ? 'faceOff' : 'face', 15)
    ;(els.cam as HTMLVideoElement).style.opacity = s.layout.hidden ? '0.25' : '1'

    // Mic meter — this used to live in the page; the floating bar is now the
    // only control surface, so the feedback moves here with it.
    const level = s.micMuted ? 0 : Math.min(100, s.micLevel * 180)
    els.level.style.width = `${level}%`

    els.map.style.aspectRatio = String(s.aspect || 16 / 9)
    els.pip.style.left = `${s.layout.x * 100}%`
    els.pip.style.top = `${s.layout.y * 100}%`
    const dot = Math.max(8, CAM_SIZE * els.map.clientWidth)
    els.pip.style.width = `${dot}px`
    els.pip.style.height = `${dot}px`
  }

  close() {
    this.win?.close()
    this.win = null
  }
}

const HTML = `
<div class="wrap">
  <div class="head">
    <span id="fb-dot" class="dot rec"></span>
    <span id="fb-time" class="time">0:00</span>
    <video id="fb-cam" class="cam"></video>
  </div>
  <div class="meter"><i id="fb-level"></i></div>
  <div class="mapwrap">
    <div id="fb-map" class="map" title="Drag to move your camera bubble"><div id="fb-pip" class="pip"></div></div>
  </div>
  <div class="row">
    <button id="fb-mic" class="btn" aria-label="Mute microphone">${iconSvg('mic', 15)}</button>
    <button id="fb-camtoggle" class="btn" aria-label="Hide camera">${iconSvg('face', 15)}</button>
    <button id="fb-pause" class="btn" aria-label="Pause recording">${iconSvg('pause', 15)}</button>
    <button id="fb-stop" class="btn stop" aria-label="Stop recording">${iconSvg('stop', 15)}</button>
  </div>
</div>`

const CSS = `
* { box-sizing: border-box; margin: 0; }
body { font: 13px/1.4 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
  background: #14131a; color: #f2f0f7; user-select: none; -webkit-user-select: none; }
.wrap { padding: 10px; display: flex; flex-direction: column; gap: 9px; height: 100vh; }
.head { display: flex; align-items: center; gap: 8px; }
.dot { width: 9px; height: 9px; border-radius: 50%; flex: none; }
.dot.rec { background: #ff4d5e; animation: pulse 1.6s ease-in-out infinite; }
.dot.paused { background: #f5a524; }
@keyframes pulse { 0%,100% { opacity: 1 } 50% { opacity: .25 } }
.time { font-variant-numeric: tabular-nums; font-weight: 600; letter-spacing: .02em; }
.cam { margin-left: auto; width: 46px; height: 46px; border-radius: 50%; object-fit: cover;
  background: #000; transform: scaleX(-1); border: 1px solid rgba(255,255,255,.2); }
.mapwrap { flex: 1; display: flex; align-items: center; }
.map { position: relative; width: 100%; aspect-ratio: 16/9; background: #26242f;
  border: 1px solid rgba(255,255,255,.12); border-radius: 6px; cursor: crosshair;
  background-image: linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px);
  background-size: 25% 25%; touch-action: none; }
.pip { position: absolute; transform: translate(-50%, -50%); background: #7c5cff; border-radius: 50%;
  border: 1.5px solid #fff; box-shadow: 0 2px 6px rgba(0,0,0,.5); pointer-events: none; }
.meter { height: 4px; border-radius: 3px; background: #26242f; overflow: hidden; }
.meter i { display: block; height: 100%; width: 0%; background: #34d399; transition: width .08s linear; }
.row { display: flex; gap: 6px; }
.btn { flex: 1; height: 32px; border: 1px solid rgba(255,255,255,.14); background: #262433;
  color: #fff; border-radius: 7px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; }
.ico { display: block; }
.btn:hover { background: #322f42; }
.btn.off { opacity: .45; }
.btn.stop { background: #ff4d5e; border-color: #ff4d5e; color: #fff; }
.btn.stop:hover { background: #ff3345; }
`
